interface BackendErrorResponse {
    readonly message?: unknown;
    readonly details?: unknown;
}

export type AiBotRoomFailureReason =
    | 'AI_BOT_ROOM_FORBIDDEN'
    | 'AI_BOT_ROOM_SETUP_IN_PROGRESS'
    | 'AI_BOT_JOIN_TIMEOUT'
    | 'AI_BOT_ROOM_REPAIR_FAILED';

export class AiBotRoomError extends Error {
    constructor(
        readonly status: number,
        message: string,
        readonly reason?: AiBotRoomFailureReason,
    ) {
        super(message);
        this.name = 'AiBotRoomError';
    }
}

export const createAiBotRoomError = (
    status: number,
    statusText: string,
    responseBody: string,
): AiBotRoomError => {
    const backendError = parseBackendError(responseBody);
    const message = backendError?.message ?? (statusText || 'PhoneHQ AI room operation failed');
    return new AiBotRoomError(status, message, backendError?.reason);
};

export const getAiBotRoomErrorDescription = (error: unknown, fallback: string): string =>
    error instanceof AiBotRoomError ? error.message : fallback;

const parseBackendError = (
    responseBody: string,
): {readonly message: string | null; readonly reason?: AiBotRoomFailureReason} | null => {
    if (!responseBody) return null;

    try {
        const parsedBody: unknown = JSON.parse(responseBody);
        if (!isBackendErrorResponse(parsedBody)) return null;

        const message = typeof parsedBody.message === 'string' ? parsedBody.message.trim() || null : null;
        return {message, reason: parseFailureReason(parsedBody.details)};
    } catch {
        return null;
    }
};

const parseFailureReason = (details: unknown): AiBotRoomFailureReason | undefined => {
    if (typeof details !== 'object' || details === null || !('reason' in details)) return undefined;

    const reason = details.reason;
    switch (reason) {
        case 'AI_BOT_ROOM_FORBIDDEN':
        case 'AI_BOT_ROOM_SETUP_IN_PROGRESS':
        case 'AI_BOT_JOIN_TIMEOUT':
        case 'AI_BOT_ROOM_REPAIR_FAILED':
            return reason;
        default:
            return undefined;
    }
};

const isBackendErrorResponse = (value: unknown): value is BackendErrorResponse =>
    typeof value === 'object' && value !== null;
